import type { Coordinate, Pipe } from './network';

const cross = (a: Coordinate, b: Coordinate, c: Coordinate) =>
  (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
const EPS = 1e-14;
function onSegment(p: Coordinate, a: Coordinate, b: Coordinate) {
  return Math.abs(cross(a, b, p)) < EPS &&
    p[0] >= Math.min(a[0], b[0]) - EPS && p[0] <= Math.max(a[0], b[0]) + EPS &&
    p[1] >= Math.min(a[1], b[1]) - EPS && p[1] <= Math.max(a[1], b[1]) + EPS;
}
function intersects(a: Coordinate, b: Coordinate, c: Coordinate, d: Coordinate) {
  const abC = cross(a, b, c), abD = cross(a, b, d), cdA = cross(c, d, a), cdB = cross(c, d, b);
  return (abC * abD < 0 && cdA * cdB < 0) ||
    onSegment(a, c, d) || onSegment(b, c, d) || onSegment(c, a, b) || onSegment(d, a, b);
}
function inside(point: Coordinate, polygon: Coordinate[]) {
  let result = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i], b = polygon[j];
    if (onSegment(point, a, b)) return true;
    if ((a[1] > point[1]) !== (b[1] > point[1]) &&
      point[0] < (b[0] - a[0]) * (point[1] - a[1]) / (b[1] - a[1]) + a[0]) result = !result;
  }
  return result;
}
/** ≥3 points, ≥1 m²: reject clicks, straight strokes and insignificant jitter. */
export function validLasso(polygon: Coordinate[]) {
  if (polygon.length < 3 || polygon.some(p => !p.every(Number.isFinite))) return false;
  const origin = polygon[0], scale = 111195 ** 2 * Math.cos(origin[1] * Math.PI / 180);
  const area = polygon.reduce((sum, p, i) => sum + cross(origin, p, polygon[(i + 1) % polygon.length]), 0);
  return Math.abs(area * scale / 2) >= 1;
}
/** Inclusive intersection: contains vertices OR crosses/touches any polygon boundary. */
export function pipeTouchesLasso(pipe: Pipe, polygon: Coordinate[]) {
  if (!validLasso(polygon)) return false;
  if (pipe.coordinates.some(p => inside(p, polygon))) return true;
  return pipe.coordinates.slice(1).some((b, i) =>
    polygon.some((c, j) => intersects(pipe.coordinates[i], b, c, polygon[(j + 1) % polygon.length])));
}
export const pipesInLasso = (pipes: Pipe[], polygon: Coordinate[]) =>
  pipes.filter(pipe => pipeTouchesLasso(pipe, polygon)).map(pipe => pipe.id);
